// https://chatgpt.com/c/691bb5b7-79ac-8333-a6ad-26bd1ab69541
"use client";

import { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { CardContent } from "@/components/ui/card";
import { useCartStore, selectItemQuantity } from "@/lib/cart/cart-store";
import { displayCurrency } from "@/lib/utils";
import AddToCartControls from "./add-to-cart-controls";
import { SELECTION_UNIT_PIECE, SELECTION_UNIT_KG, KG_QUANTITY_STEP } from "@/constants/misc";

export default function CatalogItemSnippet({
  item,
}: { item: any; }) {
  const addItem = useCartStore((s) => s.addItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);

  if (!item) {
    return null;
  }
  const productId = String(item.id); // TODO Explain why converting product id to string
  const selector = useMemo(() => selectItemQuantity(productId), [productId]);
  const quantity = useCartStore(selector);
  const handleAdd = () => {
    addItem(productId, item.default_selection_unit || SELECTION_UNIT_PIECE, item.measurement_unit_for_price || "", item.can_be_purchased_by_unit ?? true, item.can_be_purchased_by_kg ?? false, { displayPrice: item.display_price ?? null, pricePerPiece: item.price_per_piece ?? null, pricePerKg: item.price_per_kg ?? null, name: item.title, imageUrl: item.image_url });
  };

  const unit = item.default_selection_unit || SELECTION_UNIT_PIECE;
  const step = unit === SELECTION_UNIT_KG ? KG_QUANTITY_STEP : 1;

  const handleDecrement = () => {
    updateQuantity(productId, Math.round((quantity - step) * 10) / 10);
  };

  const handleIncrement = () => {
    updateQuantity(productId, Math.round((quantity + step) * 10) / 10);
  };

  const handleQuantityChange = (qty: number) => {
    if (qty <= 0) {
      updateQuantity(productId, 0);
    } else {
      updateQuantity(productId, Math.round(qty * 10) / 10);
    }
  };

  const priceMeasurementUnit = (item.measurement_unit_for_price && item.measurement_unit_for_price.length > 0) ? `/${item.measurement_unit_for_price}` : '';
  const displayPrice = item.display_price || item.price;
  const isOutOfStock = item.is_in_stock === false;
  const showUnit = true; //(item.can_be_purchased_by_unit && item.can_be_purchased_by_kg) || item.default_selection_unit === SELECTION_UNIT_KG;

  return (
    <Link
      href={`/producto/${item.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl cursor-pointer"
    >
      {/* Image */}
      <div className="relative w-full">
        <div className="relative mx-auto w-full aspect-square">
          <Image
            src={item.image_url}
            alt={item.title}
            loading="lazy"
            fill
            className="object-cover"
            sizes="(min-width: 768px) 8rem, 32vw"
          />
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/60" />
          )}
        </div>

        {!isOutOfStock && (
          <AddToCartControls
            quantity={quantity}
            onAdd={handleAdd}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            onQuantityChange={handleQuantityChange}
            unit={showUnit ? item.default_selection_unit : undefined}
            maxQuantity={unit === SELECTION_UNIT_KG ? (item.max_kg ?? null) : (item.max_units ?? null)}
          />
        )}

        {isOutOfStock && (
          <div className="absolute top-2 left-2 rounded-md bg-gray-600 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm">
            Agotado
          </div>
        )}

        {!isOutOfStock && !!item.percent_off && item.percent_off > 1 && (
          <div className="absolute top-2 left-2 rounded-md bg-red-500 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm">
            {item.percent_off}% off
          </div>
        )}
      </div>

      {/* Text content */}
      <CardContent className="space-y-0 p-0.5">

        {/* Title */}
        <div className="text-sm font-semibold line-clamp-2 overflow-hidden pt-0.5 text-gray-900">
          {item.title}
        </div>

        {/* Price */}
        <div className="text-sm font-normal pt-0.5 text-gray-800">
          {!!item.percent_off && (<span><span className="text-red-600">{displayCurrency(displayPrice)}{priceMeasurementUnit}</span> <span className="line-through">{displayCurrency(item.original_display_price)}{priceMeasurementUnit}</span></span>)}
          {!item.percent_off && (<span className="text-gray-800">{displayCurrency(displayPrice)}{priceMeasurementUnit}</span>)}
        </div>

        {/* Subtitle (e.g. variety) */}
        {item.subtitle && (
          <div className="truncate text-sm text-muted-foreground">
            {item.subtitle}
          </div>
        )}

        {/* TODO: Add approx quantity in kg if adding to cart by unit and vice-versa. See addToCartWidget in native app code. */}
        {false && (
          <div className="pt-0.5 text-xs leading-snug text-muted-foreground">
            <div>Aprox. 0.5kg</div>
          </div>
        )}
      </CardContent>
    </Link>
  );
}
